import { computed, observable } from 'mobx'
import { once } from 'lodash-es'
import { Model, type ModelProps } from '@/infra/model'
import { Id } from '@/infra/id'
import { unixEpoch } from '@/utils/date-util'
import { generateAvatarFallback } from '@/utils/image-util'

export interface ProviderMembership {
  workspaceId: Id
  jobCapacity: number
}

export type ProviderStatus =
  'Active' | 'Decommissioned' | 'Suspended' | 'OnLeave' | 'Null'

export type ProviderPtinActiveStatus = 'Active' | 'Inactive' | 'NotApplicable'

export type ProviderType = 'CPA' | 'EA' | 'NotApplicable'

export type ProviderProps = ModelProps<Provider>

export class Provider extends Model<Provider> {
  @observable accessor userPublicId: Id = Id.none
  @observable accessor givenName = ''
  @observable accessor familyName = ''
  @observable accessor email = ''
  @observable accessor phone = ''
  @observable accessor picture = ''
  @observable accessor workspaces: ProviderMembership[] = []
  @observable accessor globalJobCapacity = 50
  @observable accessor softwareExperience: string[] = []
  @observable accessor softwareOwned: string[] = []
  @observable accessor supplementalSoftwareExperience: string[] = []
  @observable accessor yearsExperience = 0
  @observable accessor stateRegion = ''
  @observable accessor activePtin: ProviderPtinActiveStatus = 'NotApplicable'
  @observable accessor providerStatus: ProviderStatus = 'Null'
  @observable accessor proType: ProviderType = 'NotApplicable'
  @observable accessor proManager = ''

  @observable accessor specialties: string[] = []
  @observable accessor dateCreated: Date = unixEpoch()
  @observable accessor dateModified: Date = unixEpoch()

  /**
   * Default value to use for provider.
   */
  static get none() {
    return lazyNone()
  }

  /**
   * Returns the given and family names together.
   */
  @computed
  get displayName() {
    return `${this.givenName} ${this.familyName}`.trim()
  }

  /**
   * Returns either the user-determined picture or a placeholder.
   */
  @computed
  get avatar() {
    return (
      this.picture || generateAvatarFallback(this.displayName || this.email)
    )
  }
}

const lazyNone = once(() => new Provider())
